import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useCVStore } from '../store/cvStore'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Card, CardContent } from '../components/ui/card'
import { Textarea } from '../components/ui/textarea'
import { Label } from '../components/ui/label'
import { toast } from 'sonner'
import {
  Save, Loader2, Plus, Trash2, Sparkles, BarChart3, FileText, ArrowLeft,
  User, Briefcase, FolderGit2, GraduationCap, BookOpen, Wrench
} from 'lucide-react'
import type { CVData } from '../types'
import HTMLCVPreview from '../components/HTMLCVPreview'

const emptyCV: CVData = {
  personalInfo: { name: '', email: '' },
  summary: '',
  workExperience: [],
  projects: [],
  education: [],
  courses: [],
  skills: [],
}

type SectionKey = 'personalInfo' | 'summary' | 'workExperience' | 'projects' | 'education' | 'courses' | 'skills'

const sectionTabs: { key: SectionKey; label: string; icon: any }[] = [
  { key: 'personalInfo', label: 'Personal Info', icon: User },
  { key: 'summary', label: 'Summary', icon: Sparkles },
  { key: 'workExperience', label: 'Work Experience', icon: Briefcase },
  { key: 'projects', label: 'Projects', icon: FolderGit2 },
  { key: 'education', label: 'Education', icon: GraduationCap },
  { key: 'courses', label: 'Courses', icon: BookOpen },
  { key: 'skills', label: 'Skills', icon: Wrench },
]

function EditableList({ items, onUpdate, onAdd, onRemove, fields, section }: {
  items: any[]
  onUpdate: (index: number, field: string, value: string) => void
  onAdd: () => void
  onRemove: (index: number) => void
  fields: { key: string, label: string, multiline?: boolean }[]
  section: string
}) {
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="p-3 border rounded-lg space-y-2 relative group">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-1 right-1 text-red-400 opacity-0 group-hover:opacity-100"
            onClick={() => onRemove(i)}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
          {fields.map((f) => (
            <div key={f.key}>
              <Label className="text-xs text-gray-500">{f.label}</Label>
              {f.multiline ? (
                <Textarea
                  value={item[f.key] || ''}
                  onChange={(e) => onUpdate(i, f.key, e.target.value)}
                  className="mt-1 min-h-[60px]"
                />
              ) : (
                <Input
                  value={item[f.key] || ''}
                  onChange={(e) => onUpdate(i, f.key, e.target.value)}
                  className="mt-1"
                />
              )}
            </div>
          ))}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={onAdd} className="w-full gap-1">
        <Plus className="w-3 h-3" /> Add {section}
      </Button>
    </div>
  )
}

export default function CVEditorPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const {
    currentCV, cvData, loading,
    fetchCV, saveCV, updateCV, setCVData, clearCurrent,
    addWorkExperience, removeWorkExperience,
    addProject, removeProject,
    addEducation, removeEducation,
    addCourse, removeCourse,
    optimizeSection, paraphraseSection,
  } = useCVStore()

  const [saving, setSaving] = useState(false)
  const [jobDescription, setJobDescription] = useState('')
  const [activeSection, setActiveSection] = useState<SectionKey>('personalInfo')
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit')

  useEffect(() => {
    if (id) {
      fetchCV(id)
    } else {
      clearCurrent()
      setCVData(emptyCV)
    }
  }, [id])

  useEffect(() => {
    if (currentCV?.job_description) {
      setJobDescription(currentCV.job_description)
    }
  }, [currentCV])

  const data = cvData || emptyCV

  const updatePersonalInfo = useCallback((field: string, value: string) => {
    if (!cvData) return
    setCVData({ ...cvData, personalInfo: { ...cvData.personalInfo, [field]: value } })
  }, [cvData, setCVData])

  const updateSummary = useCallback((value: string) => {
    if (!cvData) return
    setCVData({ ...cvData, summary: value })
  }, [cvData, setCVData])

  const updateSkills = useCallback((value: string) => {
    if (!cvData) return
    setCVData({ ...cvData, skills: value.split(',').map((s: string) => s.trim()).filter(Boolean) })
  }, [cvData, setCVData])

  const updateWork = useCallback((index: number, field: string, value: string) => {
    if (!cvData) return
    const we = [...cvData.workExperience]
    we[index] = { ...we[index], [field]: value }
    setCVData({ ...cvData, workExperience: we })
  }, [cvData, setCVData])

  const updateProject = useCallback((index: number, field: string, value: string) => {
    if (!cvData) return
    const p = [...cvData.projects]
    p[index] = { ...p[index], [field]: value }
    setCVData({ ...cvData, projects: p })
  }, [cvData, setCVData])

  const updateEducation = useCallback((index: number, field: string, value: string) => {
    if (!cvData) return
    const e = [...cvData.education]
    e[index] = { ...e[index], [field]: value }
    setCVData({ ...cvData, education: e })
  }, [cvData, setCVData])

  const updateCourse = useCallback((index: number, field: string, value: string) => {
    if (!cvData) return
    const c = [...cvData.courses]
    c[index] = { ...c[index], [field]: value }
    setCVData({ ...cvData, courses: c })
  }, [cvData, setCVData])

  const handleSave = async () => {
    if (!cvData) return
    setSaving(true)
    try {
      if (id) {
        await updateCV(id, cvData, jobDescription || undefined)
        toast.success('CV updated')
      } else {
        const newId = await saveCV(cvData, jobDescription || undefined)
        navigate(`/editor/${newId}`, { replace: true })
        toast.success('CV created')
      }
    } catch {
      toast.error('Failed to save CV')
    } finally {
      setSaving(false)
    }
  }

  const handleOptimizeSection = async (section: string, content: any) => {
    if (!cvData) return
    try {
      const result = await optimizeSection(cvData, section, content, jobDescription || undefined)
      if (result?.optimized) {
        const updated = { ...cvData }
        if (section === 'summary') {
          updated.summary = result.optimized
        } else if (section in updated) {
          ;(updated as any)[section] = result.optimized
        }
        setCVData(updated)
        toast.success(`${section} section optimized`)
      }
    } catch {
      toast.error('Optimization failed')
    }
  }

  const handleParaphraseSection = async (section: string) => {
    if (!cvData) return
    try {
      const result = await paraphraseSection(cvData, section, jobDescription || undefined)
      if (result?.paraphrased) {
        const updated = { ...cvData }
        if (section === 'summary') {
          updated.summary = result.paraphrased
        } else if (section in updated) {
          ;(updated as any)[section] = result.paraphrased
        }
        setCVData(updated)
        toast.success(`${section} section paraphrased`)
      }
    } catch {
      toast.error('Paraphrase failed')
    }
  }

  const handlePreview = () => {
    setActiveTab('preview')
  }

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'personalInfo':
        return (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Full Name</Label>
              <Input value={data.personalInfo.name} onChange={(e) => updatePersonalInfo('name', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Email</Label>
              <Input value={data.personalInfo.email} onChange={(e) => updatePersonalInfo('email', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Phone</Label>
              <Input value={data.personalInfo.phone || ''} onChange={(e) => updatePersonalInfo('phone', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">LinkedIn</Label>
              <Input value={data.personalInfo.linkedin || ''} onChange={(e) => updatePersonalInfo('linkedin', e.target.value)} className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-gray-500">Portfolio</Label>
              <Input value={data.personalInfo.portfolio || ''} onChange={(e) => updatePersonalInfo('portfolio', e.target.value)} className="mt-1" />
            </div>
          </div>
        )

      case 'summary':
        return (
          <>
            <Textarea
              value={data.summary || ''}
              onChange={(e) => updateSummary(e.target.value)}
              className="min-h-[180px]"
              placeholder="Write a brief professional summary..."
            />
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleOptimizeSection('summary', data.summary)} className="gap-1">
                <Sparkles className="w-3 h-3" /> Optimize
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleParaphraseSection('summary')} className="gap-1">
                <Sparkles className="w-3 h-3" /> Paraphrase
              </Button>
            </div>
          </>
        )

      case 'workExperience':
        return (
          <EditableList
            items={data.workExperience}
            onUpdate={updateWork}
            onAdd={addWorkExperience}
            onRemove={removeWorkExperience}
            fields={[
              { key: 'company', label: 'Company' },
              { key: 'role', label: 'Role' },
              { key: 'duration', label: 'Duration' },
              { key: 'description', label: 'Description', multiline: true },
            ]}
            section="Experience"
          />
        )

      case 'projects':
        return (
          <EditableList
            items={data.projects}
            onUpdate={updateProject}
            onAdd={addProject}
            onRemove={removeProject}
            fields={[
              { key: 'name', label: 'Project Name' },
              { key: 'duration', label: 'Duration' },
              { key: 'link', label: 'Link' },
              { key: 'description', label: 'Description', multiline: true },
            ]}
            section="Project"
          />
        )

      case 'education':
        return (
          <EditableList
            items={data.education}
            onUpdate={updateEducation}
            onAdd={addEducation}
            onRemove={removeEducation}
            fields={[
              { key: 'institution', label: 'Institution' },
              { key: 'major', label: 'Major' },
              { key: 'year', label: 'Year' },
              { key: 'description', label: 'Description', multiline: true },
            ]}
            section="Education"
          />
        )

      case 'courses':
        return (
          <EditableList
            items={data.courses}
            onUpdate={updateCourse}
            onAdd={addCourse}
            onRemove={removeCourse}
            fields={[
              { key: 'name', label: 'Course Name' },
              { key: 'institution', label: 'Institution' },
              { key: 'year', label: 'Year' },
              { key: 'link', label: 'Link' },
              { key: 'description', label: 'Description', multiline: true },
            ]}
            section="Course"
          />
        )

      case 'skills':
        return (
          <div>
            <Label className="text-xs text-gray-500">Skills (comma-separated)</Label>
            <Textarea
              value={data.skills.join(', ')}
              onChange={(e) => updateSkills(e.target.value)}
              className="mt-1 min-h-[120px]"
              placeholder="JavaScript, React, Node.js, ..."
            />
          </div>
        )
    }
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">
            {id ? currentCV?.name || 'Edit CV' : 'Create New CV'}
          </h1>
        </div>
        <div className="flex gap-2">
          {activeTab === 'preview' ? (
            <Button variant="outline" size="sm" onClick={() => setActiveTab('edit')} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Edit
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={handlePreview} className="gap-2">
              <FileText className="w-4 h-4" />
              Preview
            </Button>
          )}
          <Button size="sm" onClick={handleSave} disabled={saving || loading} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </Button>
        </div>
      </div>

      {activeTab === 'preview' ? (
        <HTMLCVPreview
          cv={data}
          onSave={handleSave}
          isSaving={saving}
          cvName={currentCV?.name}
        />
      ) : (
        <div className="flex gap-6">
          {/* Vertical Tab Sidebar */}
          <div className="w-56 shrink-0">
            <nav className="space-y-1">
              {sectionTabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveSection(tab.key)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      activeSection === tab.key
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                )
              })}
            </nav>

            <div className="my-4 border-t" />

            <button
              onClick={() => navigate(id ? `/editor/${id}/analysis` : '/editor/analysis')}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              AI Analysis
            </button>
          </div>

          {/* Section Content */}
          <div className="flex-1 min-w-0">
            <Card>
              <CardContent className="pt-6 space-y-4">
                {renderSectionContent()}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  )
}
